import { useEffect, useMemo, useRef, useState } from 'react';
import { assetImportApi, type AssetImportPreview, type PreviewRow, type RowAction } from '../lib/api/asset-import';
import { formatVND } from '../lib/format';

interface Props {
  open: boolean;
  onClose: () => void;
  onDone: () => void;
}

const HINT_TEXT = 'Import ghi tăng TSCĐ. System tự phân loại: TS chưa có → tạo mới, TS đã có → cập nhật thông tin (giữ nguyên % KH hiện tại). Chỉ dòng hợp lệ mới được tick.';

const ACTION_META: Record<RowAction, { label: string; className: string }> = {
  create: { label: 'Tạo mới', className: 'aim-badge-create' },
  update: { label: 'Cập nhật', className: 'aim-badge-update' },
  skip: { label: 'Bỏ qua', className: 'aim-badge-skip' },
  error: { label: 'Lỗi', className: 'aim-badge-error' },
};

export function AssetImportModal({ open, onClose, onDone }: Props) {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<AssetImportPreview | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [result, setResult] = useState<{ created: number; updated: number; errors: number } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    setFile(null); setPreview(null); setSelected(new Set());
    setLoading(false); setCommitting(false); setErr(null); setResult(null);
  }, [open]);

  const importable = useMemo(
    () => (preview?.rows ?? []).filter((r) => r.action === 'create' || r.action === 'update'),
    [preview],
  );

  useEffect(() => {
    if (!preview) return;
    setSelected(new Set(importable.map((r) => r.code)));
  }, [preview, importable]);

  if (!open) return null;

  const handleFile = (f: File | null) => {
    setFile(f);
    setPreview(null);
    setErr(null);
    setResult(null);
  };

  const runPreview = async () => {
    if (!file) return;
    setLoading(true); setErr(null);
    try {
      const p = await assetImportApi.preview(file);
      setPreview(p);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally { setLoading(false); }
  };

  const runCommit = async () => {
    if (!preview) return;
    const rows = importable
      .filter((r) => selected.has(r.code))
      .map((r) => ({ code: r.code, data: r.data, action: r.action as 'create' | 'update' }));
    if (rows.length === 0) { setErr('Chưa chọn dòng nào để import.'); return; }
    setCommitting(true); setErr(null);
    try {
      const res = await assetImportApi.commit(rows);
      setResult({ created: res.created, updated: res.updated, errors: res.errors.length });
      onDone();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally { setCommitting(false); }
  };

  const toggle = (code: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code); else next.add(code);
      return next;
    });
  };
  const toggleAll = () => {
    setSelected(selected.size === importable.length ? new Set() : new Set(importable.map((r) => r.code)));
  };

  const downloadTemplate = async () => {
    try { await assetImportApi.downloadTemplate(); }
    catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
  };

  const isStep1 = !preview && !result;
  const modalWidth = isStep1 ? 560 : 960;

  return (
    <div className="cvm-backdrop" onClick={onClose}>
      <div
        className={`cvm-modal sim-modal ${isStep1 ? 'sim-modal-step1' : ''}`}
        style={{ width: modalWidth, maxWidth: '96vw' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sim-head">
          <div className="sim-head-left">
            <div className="sim-head-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2" strokeLinecap="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" x2="12" y1="3" y2="15" /></svg>
            </div>
            <div>
              <h2>Import ghi tăng TSCĐ từ Excel</h2>
              <p>Upload file Excel theo template · System auto validate + preview trước import</p>
            </div>
          </div>
          <button type="button" className="sim-close" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
          </button>
        </div>

        <div className="sim-body">
          {isStep1 && (
            <>
              <div className="sim-info-banner">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#1d4ed8" strokeWidth="2" strokeLinecap="round"><path d="M12 8v4M12 16h.01" /><circle cx="12" cy="12" r="10" /></svg>
                <div>{HINT_TEXT}</div>
              </div>

              <div className="sim-template-row">
                <div className="sim-template-info">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2" strokeLinecap="round"><path d="M14 3v4a1 1 0 0 0 1 1h4" /><path d="M17 21H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7l5 5v11a2 2 0 0 1-2 2z" /></svg>
                  <span>Template: <b>template-ghi-tang-tscd.xlsx</b></span>
                </div>
                <button
                  type="button"
                  className="sim-template-dl"
                  onClick={(e) => { e.stopPropagation(); downloadTemplate(); }}
                >
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /></svg>
                  Tải template
                </button>
              </div>

              <div
                className={`sim-dropzone ${file ? 'has-file' : ''}`}
                onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('dragging'); }}
                onDragLeave={(e) => e.currentTarget.classList.remove('dragging')}
                onDrop={(e) => {
                  e.preventDefault();
                  e.currentTarget.classList.remove('dragging');
                  const f = e.dataTransfer.files?.[0];
                  if (f) handleFile(f);
                }}
                onClick={() => inputRef.current?.click()}
              >
                <input
                  ref={inputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  style={{ display: 'none' }}
                  onChange={(e) => handleFile(e.target.files?.[0] ?? null)}
                />
                {file ? (
                  <>
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="1.5" strokeLinecap="round"><path d="M14 3v4a1 1 0 0 0 1 1h4" /><path d="M17 21H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7l5 5v11a2 2 0 0 1-2 2z" /></svg>
                    <div className="sim-dropzone-main"><b>{file.name}</b></div>
                    <div className="sim-dropzone-sub">
                      {(file.size / 1024).toFixed(1)} KB · Click <button type="button" className="sim-dropzone-link" onClick={(e) => { e.stopPropagation(); handleFile(null); }}>xoá</button> để chọn file khác
                    </div>
                  </>
                ) : (
                  <>
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" x2="12" y1="3" y2="15" /></svg>
                    <div className="sim-dropzone-main">Kéo thả file Excel vào đây</div>
                    <div className="sim-dropzone-sub">hoặc <span className="sim-dropzone-link">chọn từ máy</span></div>
                    <div className="sim-dropzone-hint">.xlsx · .xls · tối đa 20MB</div>
                  </>
                )}
              </div>
            </>
          )}

          {preview && !result && (
            <div className="sim-preview">
              <div className="sim-info-banner sim-info-banner-plain">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#1d4ed8" strokeWidth="2" strokeLinecap="round"><path d="M12 8v4M12 16h.01" /><circle cx="12" cy="12" r="10" /></svg>
                <div>{HINT_TEXT}</div>
              </div>

              <PreviewSummary preview={preview} selectedCount={selected.size} />

              {importable.length > 0 ? (
                <>
                  <div style={sectionTitle}>
                    <span>Sẽ import ({importable.length})</span>
                    <button type="button" className="pay-btn-outline" onClick={toggleAll}>
                      {selected.size === importable.length ? 'Bỏ chọn tất cả' : 'Chọn tất cả'}
                    </button>
                  </div>
                  <div style={tableWrap}>
                    <table style={tblStyle}>
                      <thead>
                        <tr>
                          <th style={{ width: 32 }}></th>
                          <th style={{ width: 90 }}>Mã TS</th>
                          <th>Tên · Loại</th>
                          <th style={{ width: 130, textAlign: 'right' }}>Nguyên giá</th>
                          <th style={{ width: 100 }}>Thao tác</th>
                        </tr>
                      </thead>
                      <tbody>
                        {importable.map((r) => (
                          <PreviewRowView
                            key={r.code}
                            row={r}
                            checked={selected.has(r.code)}
                            onToggle={() => toggle(r.code)}
                          />
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                <div style={{ marginTop: 10, color: '#94a3b8', fontSize: 13, fontStyle: 'italic' }}>
                  Không có dòng nào hợp lệ để import.
                </div>
              )}

              {preview.toError > 0 && (
                <>
                  <div style={{ ...sectionTitle, color: '#b91c1c' }}>Có lỗi ({preview.toError})</div>
                  <div style={tableWrap}>
                    <table style={tblStyle}>
                      <thead>
                        <tr>
                          <th style={{ width: 60 }}>Dòng</th>
                          <th style={{ width: 90 }}>Mã TS</th>
                          <th>Tên</th>
                          <th>Lỗi</th>
                        </tr>
                      </thead>
                      <tbody>
                        {preview.rows.filter((r) => r.action === 'error').map((r) => (
                          <tr key={r.rowIndex}>
                            <td style={{ color: '#64748b' }}>{r.rowIndex}</td>
                            <td style={{ fontFamily: 'monospace', color: '#334155' }}>{r.code}</td>
                            <td>{r.data.name || '—'}</td>
                            <td style={{ color: '#b91c1c' }}>{r.errors.join(' · ')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div style={{ marginTop: 8, fontSize: 12, color: '#64748b' }}>
                    Sửa file Excel rồi upload lại. Các dòng lỗi sẽ bị bỏ qua.
                  </div>
                </>
              )}
            </div>
          )}

          {result && (
            <div style={{ marginTop: 20, padding: 16, background: '#f0fdf4', borderRadius: 8, border: '1px solid #86efac' }}>
              <div style={{ fontSize: 16, fontWeight: 600, color: '#166534' }}>Import xong ✓</div>
              <div style={{ marginTop: 6, fontSize: 14, color: '#166534' }}>
                Đã tạo mới <b>{result.created}</b> · Cập nhật <b>{result.updated}</b>
                {result.errors > 0 && <> · Lỗi <b style={{ color: '#b91c1c' }}>{result.errors}</b></>}
              </div>
            </div>
          )}

          {err && <div className="cvm-error" style={{ marginTop: 12 }}>{err}</div>}
        </div>

        <div className="sim-foot">
          <button type="button" className="sim-btn-cancel" onClick={onClose} disabled={loading || committing}>
            {result ? 'Đóng' : 'Hủy'}
          </button>
          {isStep1 && (
            <button
              type="button"
              className="sim-btn-primary"
              onClick={runPreview}
              disabled={!file || loading}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M20 6 9 17l-5-5" /></svg>
              {loading ? 'Đang phân tích…' : 'Preview + Import'}
            </button>
          )}
          {preview && !result && (
            <button
              type="button"
              className="sim-btn-primary"
              onClick={runCommit}
              disabled={committing || selected.size === 0}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M20 6 9 17l-5-5" /></svg>
              {committing ? 'Đang import…' : `Import ${selected.size} TS`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function PreviewSummary({ preview, selectedCount }: { preview: AssetImportPreview; selectedCount: number }) {
  return (
    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', fontSize: 13 }}>
      <Stat label="Sheet" value={preview.sheetName} />
      <Stat label="Tổng row" value={String(preview.totalRows)} />
      <Stat label="Tạo mới" value={String(preview.toCreate)} tone="green" />
      <Stat label="Cập nhật" value={String(preview.toUpdate)} tone="blue" />
      {preview.toError > 0 && <Stat label="Lỗi" value={String(preview.toError)} tone="red" />}
      <Stat label="Đã chọn" value={String(selectedCount)} tone="blue" />
    </div>
  );
}

function Stat({ label, value, tone = 'gray' }: { label: string; value: string; tone?: 'gray' | 'green' | 'red' | 'blue' }) {
  const colors: Record<string, { bg: string; fg: string }> = {
    gray: { bg: '#f1f5f9', fg: '#334155' },
    green: { bg: '#f0fdf4', fg: '#166534' },
    red: { bg: '#fef2f2', fg: '#b91c1c' },
    blue: { bg: '#eff6ff', fg: '#1e40af' },
  };
  const c = colors[tone];
  return (
    <div style={{ padding: '6px 10px', background: c.bg, color: c.fg, borderRadius: 6 }}>
      <span style={{ opacity: 0.7 }}>{label}: </span><b>{value}</b>
    </div>
  );
}

function PreviewRowView({ row, checked, onToggle }: { row: PreviewRow; checked: boolean; onToggle: () => void }) {
  const meta = ACTION_META[row.action];
  return (
    <tr>
      <td>
        <input type="checkbox" checked={checked} onChange={onToggle} />
      </td>
      <td style={{ fontFamily: 'monospace', fontWeight: 700, color: '#172033' }}>{row.code}</td>
      <td>
        <div style={{ color: '#172033', fontWeight: 500 }}>{row.data.name || '—'}</div>
        <div style={{ fontSize: 11, color: '#64748b' }}>
          {row.data.categoryCode}
          {row.categoryName && ` · ${row.categoryName}`}
          {row.data.department && ` · ${row.data.department}`}
        </div>
      </td>
      <td style={{ textAlign: 'right', fontWeight: 600, color: '#172033' }}>
        {row.data.cost != null ? formatVND(row.data.cost) : '—'}
      </td>
      <td>
        <span className={`aim-badge ${meta.className}`}>{meta.label}</span>
      </td>
    </tr>
  );
}

const sectionTitle: React.CSSProperties = {
  marginTop: 16,
  marginBottom: 8,
  fontSize: 14,
  fontWeight: 600,
  color: '#334155',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
};

const tableWrap: React.CSSProperties = {
  border: '1px solid #e2e8f0',
  borderRadius: 6,
  maxHeight: 320,
  overflow: 'auto',
};

const tblStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: 13,
};
